import { useState, useRef, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { useTranslation } from "./context/TranslationContext";
import Layout from "./components/Layout";
import Toast from "./components/ds/Toast";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import ProfilePage from "./pages/ProfilePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import EmptyPage from "./pages/EmptyPage";
import ActualHomePage from "./pages/ActualHomePage";
import JoinedMintsPage from "./pages/JoinedMintsPage";
import MessagesPage from "./pages/MessagesPage";
import SupportPage from "./pages/SupportPage";
import OAuthCallbackPage from "./pages/OAuthCallbackPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentCallbackPage from "./pages/PaymentCallbackPage";
import CheckoutReturnPage from "./pages/CheckoutReturnPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import ResourcesPage from "./pages/ResourcesPage";
import AboutPage from "./pages/AboutPage";
import NewsArticlePage from "./pages/NewsArticlePage";
import BalancePage from "./pages/BalancePage";
import PurchasesPage from "./pages/PurchasesPage";
import AffiliateMarketplacePage from "./pages/AffiliateMarketplacePage";
import ReferralLandingPage from "./pages/ReferralLandingPage";
import InviteLandingPage from "./pages/InviteLandingPage";
import ReferralsPage from "./pages/ReferralsPage";
import CampaignWindow from "./components/CampaignWindow";
import CreateBusinessWindow from "./components/CreateBusinessWindow";
import CampaignDetailsWindow from "./components/CampaignDetailsWindow";
import MintSpaceWindow from "./components/MintSpaceWindow";
import YourMintSpaceWindow from "./components/YourMintSpaceWindow";
import MintSpaceProductDetailsPage from "./pages/MintSpaceProductDetailsPage";
import SocialModal from "./components/SocialModal";
import {
  getMyProfile,
  updateMyProfile,
  mergeAuthUserAfterProfileSave,
} from "./api/profile";
import { stripAvatarCacheBuster } from "./utils/userDisplay";
import { FloatingChatProvider } from "./context/FloatingChatContext";
import { AuthModalProvider } from "./context/AuthModalContext";
import { SpaceGateProvider } from "./context/SpaceGateContext";
import { uploadAvatar, uploadBanner } from "./api/upload";
import useReferralCapture from "./hooks/useReferralCapture";
import "./App.css";
import { refusedUploadMessage } from "./utils/refusedUpload";

function AppContent() {
  const { t } = useTranslation();
  const { isAuthenticated, user, setUser } = useAuth();
  const location = useLocation();
  const [theme, setTheme] = useState("dark");
  const [audience, setAudience] = useState("creators");
  const [toast, setToast] = useState("");

  // Debug: Log route changes
  useEffect(() => {
    console.log("Route changed to:", location.pathname);
  }, [location.pathname]);

  // Pick up ?ref=<affiliate code> on any route so the referrer gets credited on join.
  useReferralCapture();

  // Profile page state
  const [bannerImage, setBannerImage] = useState(null);
  const [avatarImage, setAvatarImage] = useState(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [profileData, setProfileData] = useState({
    bio: "",
    location: "",
    website: "",
    username: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    businessType: "",
    businessTypeOther: "",
    companyIdentificationNumber: "",
    joinedMintSpacesPrivate: false,
  });
  const [socials, setSocials] = useState({
    youtube: "",
    instagram: "",
    facebook: "",
    tiktok: "",
  });
  const [socialModal, setSocialModal] = useState(null);
  const [tempSocialUrl, setTempSocialUrl] = useState("");
  const [profilePreview, setProfilePreview] = useState(true);

  const bannerInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const isDark = theme === "dark";

  // On the root, so the tokens also reach popups that portal out to <body>.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const creatorExamples = [
    { name: "TBC Bank", desc: "Fintech launches & Gen Z engagement" },
    { name: "Bank of Georgia", desc: "Lifestyle banking & rewards collabs" },
    { name: "Magti", desc: "5G rollouts with streamers" },
    { name: "Silknet", desc: "Home internet & smart home promos" },
    { name: "Tegeta Motors", desc: "Auto lifestyle & motorsport spots" },
    { name: "GPC", desc: "Health, wellness, pharmacy drops" },
    { name: "Evex Clinics", desc: "Preventive care awareness" },
    { name: "PSP", desc: "Beauty & healthcare campaigns" },
    { name: "Adjara Group", desc: "Hotels & gastro hospitality collabs" },
    { name: "Rooms Hotels", desc: "Boutique staycations & events" },
    { name: "Liberty Bank", desc: "SME stories & financial literacy" },
    { name: "Socar Georgia", desc: "Energy & mobility partnerships" },
  ];

  const editorExamples = [
    { name: "Rustavi 2", desc: "TV show recuts & promo teasers" },
    { name: "Imedi TV", desc: "News highlights & social clips" },
    { name: "Formula TV", desc: "Entertainment edits & viral cuts" },
    { name: "Mtavari", desc: "Documentary recuts & explainers" },
    { name: "TV Pirveli", desc: "Talk show highlights & snippets" },
    { name: "Post TV", desc: "Lifestyle edits & brand integrations" },
  ];

  const inbox = [
    { from: "Lia / Mintly", text: "Campaign deck ready?", time: "2m" },
    {
      from: "Tegeta Motors",
      text: "Need 15s vertical by Friday.",
      time: "10m",
    },
    { from: "GPC Health", text: "Brief updated with new CTA.", time: "1h" },
    { from: "X Factor Edits", text: "Sync on color grade?", time: "3h" },
    { from: "Mintly Ops", text: "Your invite link was opened.", time: "5h" },
  ];

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const clearToast = () => setToast("");

  const baseText = "text-label-primary";

  const backgroundStyle = { background: "var(--color-bg-base)" };

  const handleSocialChange = (field) => async (event) => {
    const value = event.target.value;
    const updatedSocials = { ...socials, [field]: value };
    setSocials(updatedSocials);

    // Save immediately when social link changes (only if authenticated, profile loaded, and not loading)
    if (isAuthenticated && profileLoaded && !isLoadingProfile) {
      try {
        await updateMyProfile({
          socials: updatedSocials,
        });
      } catch (error) {
        console.error("Failed to save social link:", error);
        if (error.response?.status !== 401) {
          setToast(t("toasts.failedToSaveSocialLink"));
        }
      }
    }
  };

  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load profile data when user is authenticated
  useEffect(() => {
    const loadProfile = async () => {
      if (isAuthenticated) {
        // Clear images immediately before loading new profile
        setBannerImage(null);
        setAvatarImage(null);

        setIsLoadingProfile(true);
        setIsInitialLoad(true);
        try {
          const response = await getMyProfile();
          if (response.success) {
            const profile = response.profile || {};
            const userData = response.user || {};
            setProfileData({
              bio: profile.bio || "",
              location: profile.location || "",
              website: profile.website || "",
              username: userData.username || "",
              firstName: userData.firstName || "",
              lastName: userData.lastName || "",
              dateOfBirth: userData.dateOfBirth || "",
              businessType: userData.businessType || "",
              businessTypeOther: userData.businessTypeOther || "",
              companyIdentificationNumber:
                userData.companyIdentificationNumber || "",
              joinedMintSpacesPrivate: !!profile.joinedMintSpacesPrivate,
            });
            setSocials({
              youtube: profile.socials?.youtube || "",
              instagram: profile.socials?.instagram || "",
              facebook: profile.socials?.facebook || "",
              tiktok: profile.socials?.tiktok || "",
            });
            // Always set images (even if null) to clear previous user's images
            // Add cache busting timestamp to prevent browser caching
            const cleanBanner = profile.bannerImage
              ? stripAvatarCacheBuster(profile.bannerImage)
              : "";
            const cleanAvatar = profile.avatarImage || profile.avatar
              ? stripAvatarCacheBuster(profile.avatarImage || profile.avatar)
              : "";
            const bannerUrl = cleanBanner
              ? `${cleanBanner}${cleanBanner.includes("?") ? "&" : "?"}_t=${Date.now()}`
              : null;
            const avatarUrl = cleanAvatar
              ? `${cleanAvatar}${cleanAvatar.includes("?") ? "&" : "?"}_t=${Date.now()}`
              : null;
            setBannerImage(bannerUrl);
            setAvatarImage(avatarUrl);
            if (setUser) {
              setUser((prev) =>
                prev
                  ? {
                      ...prev,
                      username: userData.username || prev.username,
                      businessName: userData.businessName || prev.businessName,
                      instagramVerification:
                        response.instagramVerification ?? prev.instagramVerification,
                      profile: {
                        ...(prev.profile || {}),
                        avatarImage: cleanAvatar,
                        avatar: profile.avatar || cleanAvatar,
                        socials: profile.socials || prev.profile?.socials,
                      },
                    }
                  : prev
              );
            }
          }
        } catch (error) {
          console.error("Failed to load profile:", error);
        } finally {
          setIsLoadingProfile(false);
          setProfileLoaded(true);
          // Mark initial load as complete after a short delay to prevent auto-save on load
          setTimeout(() => setIsInitialLoad(false), 500);
        }
      } else {
        // Clear profile data when user logs out
        setProfileLoaded(false);
        setIsLoadingProfile(false);
        setIsInitialLoad(true);
        setBannerImage(null);
        setAvatarImage(null);
        setProfileData({
          bio: "",
          location: "",
          website: "",
          username: "",
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          businessType: "",
          businessTypeOther: "",
          companyIdentificationNumber: "",
          joinedMintSpacesPrivate: false,
        });
        setSocials({
          youtube: "",
          instagram: "",
          facebook: "",
          tiktok: "",
        });
      }
    };
    loadProfile();
  }, [isAuthenticated, user?._id]); // Also depend on user ID to reload when user changes

  const profileRouteMatch = location.pathname.match(/^\/profile\/([^/]+)$/);
  const viewingOtherProfile =
    !!profileRouteMatch &&
    profileRouteMatch[1] !== String(user?.id ?? user?._id ?? "");

  // Auto-save profile data when it changes (debounced) - only after initial load
  useEffect(() => {
    // Don't save if: not authenticated, profile not loaded, currently loading profile, on initial load, or viewing another user's profile
    if (
      !isAuthenticated ||
      !profileLoaded ||
      isLoadingProfile ||
      isInitialLoad ||
      viewingOtherProfile
    )
      return;

    const saveTimer = setTimeout(async () => {
      try {
        // Build update object, only including image fields if they have actual values
        const updateData = {
          bio: profileData.bio,
          location: profileData.location,
          website: profileData.website,
          username: profileData.username,
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          dateOfBirth: profileData.dateOfBirth,
          businessType: profileData.businessType,
          businessTypeOther: profileData.businessTypeOther,
          companyIdentificationNumber: profileData.companyIdentificationNumber,
          joinedMintSpacesPrivate: !!profileData.joinedMintSpacesPrivate,
          socials: socials,
        };

        // Only include image fields if they have actual non-empty values
        // Don't send empty strings or blob URLs - let the backend handle missing fields
        // Blob URLs start with "blob:" and should not be saved to the database
        if (
          bannerImage &&
          bannerImage.trim() !== "" &&
          !bannerImage.startsWith("blob:")
        ) {
          updateData.bannerImage = bannerImage;
        }
        if (
          avatarImage &&
          avatarImage.trim() !== "" &&
          !avatarImage.startsWith("blob:")
        ) {
          updateData.avatarImage = stripAvatarCacheBuster(avatarImage);
        }

        const res = await updateMyProfile(updateData);
        if (res?.success && setUser) {
          setUser((prev) => mergeAuthUserAfterProfileSave(prev, res, socials));
        }
      } catch (error) {
        console.error("Failed to save profile:", error);
        // Only show error if it's not a network error or if user made actual changes
        if (error.response?.status !== 401) {
          setToast(t("toasts.failedToSaveProfile"));
        }
      }
    }, 1000); // Debounce: save 1 second after user stops typing

    return () => clearTimeout(saveTimer);
  }, [
    profileData.bio,
    profileData.location,
    profileData.website,
    profileData.firstName,
    profileData.lastName,
    profileData.dateOfBirth,
    profileData.businessType,
    profileData.businessTypeOther,
    profileData.companyIdentificationNumber,
    profileData.joinedMintSpacesPrivate,
    profileData.username,
    bannerImage,
    avatarImage,
    socials,
    isAuthenticated,
    profileLoaded,
    isLoadingProfile,
    isInitialLoad,
    viewingOtherProfile,
    t,
    setUser,
  ]);

  const handleBannerChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setBannerImage(previewUrl);

    // Upload to Backblaze and save to database (only if authenticated and profile loaded)
    if (isAuthenticated && profileLoaded) {
      setIsUploadingBanner(true);
      try {
        const uploadResponse = await uploadBanner(file);
        if (uploadResponse.success) {
          await updateMyProfile({ bannerImage: uploadResponse.url });
          // Update preview with actual URL
          setBannerImage(uploadResponse.url);
          setToast(t("toasts.bannerUploaded"));
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("Failed to save banner:", error);
        setToast(refusedUploadMessage(error) || t("toasts.failedToSaveBanner"));
        // Revert preview on error
        setBannerImage(null);
      } finally {
        setIsUploadingBanner(false);
      }
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setAvatarImage(previewUrl);

    // Upload to Backblaze and save to database (only if authenticated and profile loaded)
    if (isAuthenticated && profileLoaded) {
      setIsUploadingAvatar(true);
      try {
        const uploadResponse = await uploadAvatar(file);
        if (uploadResponse.success) {
          await updateMyProfile({ avatarImage: uploadResponse.url });
          // Update preview with actual URL
          setAvatarImage(uploadResponse.url);
          if (setUser) {
            setUser((prev) =>
              prev
                ? {
                    ...prev,
                    profile: {
                      ...(prev.profile || {}),
                      avatarImage: uploadResponse.url,
                      avatar: uploadResponse.url,
                    },
                  }
                : prev
            );
          }
          setToast(t("toasts.avatarUploaded"));
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        console.error("Failed to save avatar:", error);
        setToast(refusedUploadMessage(error) || t("toasts.failedToSaveAvatar"));
        // Revert preview on error
        setAvatarImage(null);
      } finally {
        setIsUploadingAvatar(false);
      }
    }
  };

  return (
    <AuthModalProvider isDark={isDark} setToast={setToast}>
    <FloatingChatProvider isDark={isDark} setToast={setToast} user={user}>
    <SpaceGateProvider isDark={isDark} setToast={setToast}>
    <div
      className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${baseText}`}
      style={backgroundStyle}
    >
      <Routes>
        {/* One persistent Layout — page content swaps via Outlet (no chrome remount flicker) */}
        <Route
          element={
            <Layout
              isDark={isDark}
              theme={theme}
              toggleTheme={toggleTheme}
              avatarImage={avatarImage}
              setToast={setToast}
              inbox={inbox}
            >
              <Outlet />
            </Layout>
          }
        >
          <Route
            path="/auth"
            element={
              <PublicRoute allowAuthenticated={true}>
                  <AuthPage isDark={isDark} setToast={setToast} />
              </PublicRoute>
            }
          />
          <Route
            path="/"
            element={
              <PublicRoute allowAuthenticated={true}>
                <HomePage
                  isDark={isDark}
                  audience={audience}
                  setAudience={setAudience}
                  creatorExamples={creatorExamples}
                  editorExamples={editorExamples}
                  setToast={setToast}
                />
              </PublicRoute>
            }
          />
          <Route
            path="/resources"
            element={
              <PublicRoute allowAuthenticated={true}>
                <ResourcesPage isDark={isDark} setToast={setToast} />
              </PublicRoute>
            }
          />
          <Route
            path="/resources/about"
            element={
              <PublicRoute allowAuthenticated={true}>
                <AboutPage isDark={isDark} setToast={setToast} />
              </PublicRoute>
            }
          />
          <Route
            path="/resources/news/:slug"
            element={
              <PublicRoute allowAuthenticated={true}>
                <NewsArticlePage isDark={isDark} setToast={setToast} />
              </PublicRoute>
            }
          />
          {/* Bot protection is a section of About us now */}
          <Route path="/resources/bot-protection" element={<Navigate to="/resources/about#bot-protection" replace />} />

          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <ActualHomePage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/joined-mints"
            element={
              <ProtectedRoute>
                <JoinedMintsPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:userId?"
            element={
              <ProtectedRoute>
                <ProfilePage
                  isDark={isDark}
                  bannerImage={bannerImage}
                  setBannerImage={setBannerImage}
                  avatarImage={avatarImage}
                  setAvatarImage={setAvatarImage}
                  profileData={profileData}
                  setProfileData={setProfileData}
                  socials={socials}
                  setSocials={setSocials}
                  profilePreview={profilePreview}
                  setProfilePreview={setProfilePreview}
                  socialModal={socialModal}
                  setSocialModal={setSocialModal}
                  tempSocialUrl={tempSocialUrl}
                  setTempSocialUrl={setTempSocialUrl}
                  handleSocialChange={handleSocialChange}
                  handleBannerChange={handleBannerChange}
                  handleAvatarChange={handleAvatarChange}
                  bannerInputRef={bannerInputRef}
                  avatarInputRef={avatarInputRef}
                  setToast={setToast}
                  isUploadingBanner={isUploadingBanner}
                  isUploadingAvatar={isUploadingAvatar}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage isDark={isDark} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:conversationId?"
            element={
              <ProtectedRoute>
                <MessagesPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mint-space/:spaceId"
            element={
              <ProtectedRoute>
                <YourMintSpaceWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mint-space"
            element={
              <ProtectedRoute>
                <MintSpaceWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-campaign"
            element={
              <ProtectedRoute>
                <CampaignWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-business"
            element={
              <ProtectedRoute>
                <CreateBusinessWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mint-space/:spaceId/campaigns/:id"
            element={
              <ProtectedRoute>
                <YourMintSpaceWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:id"
            element={
              <ProtectedRoute>
                <CampaignDetailsWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:id/edit"
            element={
              <ProtectedRoute>
                <CampaignWindow isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <EmptyPage isDark={isDark} page="search" />
              </ProtectedRoute>
            }
          />
          <Route path="/bot-protection" element={<Navigate to="/resources/about#bot-protection" replace />} />
          <Route
            path="/invite-creators"
            element={
              <ProtectedRoute>
                <EmptyPage isDark={isDark} page="inviteCreators" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment"
            element={
              <ProtectedRoute>
                <PaymentPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/callback"
            element={
              <ProtectedRoute>
                <PaymentCallbackPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          {/* Keepz sends the buyer back here after hosted checkout, on success and
              on failure alike — the page asks our API for the verified status. */}
          <Route
            path="/checkout/return"
            element={
              <ProtectedRoute>
                <CheckoutReturnPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/balance"
            element={
              <ProtectedRoute>
                <BalancePage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases"
            element={
              <ProtectedRoute>
                <PurchasesPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <ReferralsPage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/affiliate-market"
            element={
              <ProtectedRoute>
                <AffiliateMarketplacePage isDark={isDark} setToast={setToast} />
              </ProtectedRoute>
            }
          />
          {/* Figma B19: in the shell, on the 80px rail */}
          <Route
            path="/mint-space/:spaceId/product/:productId?"
            element={<MintSpaceProductDetailsPage isDark={isDark} setToast={setToast} />}
          />
        </Route>

        <Route
          path="/auth/callback"
          element={
            <PublicRoute allowAuthenticated={true}>
              <OAuthCallbackPage setToast={setToast} />
            </PublicRoute>
          }
        />

        {/* Affiliate referral links — counts the click, then forwards to the mint space */}
        <Route path="/r/:code" element={<ReferralLandingPage isDark={isDark} />} />

        {/* Invites to Mintly itself — keeps the inviter's code, then on to sign up */}
        <Route path="/invite/:code" element={<InviteLandingPage isDark={isDark} />} />

        {/* Admin Panel - Separate Login */}
        <Route path="/admin" element={<AdminLoginPage isDark={isDark} setToast={setToast} />} />
        <Route path="/admin/dashboard" element={<AdminDashboardPage isDark={isDark} setToast={setToast} />} />

        {/* Redirect unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {toast && <Toast key={toast} message={toast} isDark={isDark} onDone={clearToast} />}

      <SocialModal
        socialModal={socialModal}
        setSocialModal={setSocialModal}
        tempSocialUrl={tempSocialUrl}
        setTempSocialUrl={setTempSocialUrl}
        handleSocialChange={handleSocialChange}
        isDark={isDark}
      />
    </div>
    </SpaceGateProvider>
    </FloatingChatProvider>
    </AuthModalProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;